"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CloudRain, Check, Info, Loader2 } from "lucide-react";

import { useAuth } from "@/app/hooks/context/AuthContext";
import { useRain } from "@/app/hooks/context/RainContext";
import SokoAmount from "@/app/components/SokoAmount";

const QUICK_AMOUNTS = [50, 100, 500, 1000];

/** 60 -> "every hour", 90 -> "every 90 minutes" — reads better than "every 60 minutes". */
const formatInterval = (minutes: number) => {
    if (minutes % 60 !== 0) return `every ${minutes} minutes`;
    const hours = minutes / 60;
    return hours === 1 ? "every hour, on the hour" : `every ${hours} hours`;
};

const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * The full rain panel behind the box: where the pot came from, who can take
 * part, and the two things a player can do about it — tip their own points in,
 * or (while the window is open) join the split.
 */
export default function RainModal({ onClose }: { onClose: () => void }) {
    const { user } = useAuth();
    const { rain, secondsLeft, joining, tipping, error, loggedIn, join, tip, clearError } = useRain();

    const [amount, setAmount] = useState(QUICK_AMOUNTS[1]);

    useEffect(() => {
        clearError();
        // Escape closes, like the reply composer in chat.
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [clearError, onClose]);

    if (!rain) return null;

    const balance = user?.soko_points ?? 0;
    const { eligibility } = rain;
    const open = rain.status === "open";
    const fromBets = Math.max(0, rain.pot - rain.base_amount - rain.tips_total);

    const clamp = (n: number) => Math.max(rain.min_tip, Math.min(rain.max_tip, Math.min(balance, Math.floor(n) || 0)));
    const canTip = loggedIn && balance >= rain.min_tip && amount >= rain.min_tip && amount <= balance && !tipping;

    const submitTip = async () => {
        if (await tip(amount)) onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[150] flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.97, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: "spring", damping: 24, stiffness: 300 }}
                className="bg-gray-900 shadow-2xl rounded-2xl w-full max-w-md my-4 sm:my-8 overflow-hidden max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 p-6 shrink-0">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    >
                        <span className="text-2xl font-bold leading-none">×</span>
                    </button>
                    <div className="flex items-center gap-2">
                        <CloudRain className="w-6 h-6 text-green-200" />
                        <h2 className="text-2xl font-bold text-white">Chat Rain</h2>
                    </div>
                    <p className="text-green-100 text-sm mt-1">
                        Every bet on SokoPlay feeds the pot. Add your own on top.
                    </p>
                </div>

                <div className="p-6 space-y-5 overflow-y-auto">
                    {/* Pot + where it came from */}
                    <div className="bg-gray-800/50 rounded-xl p-4">
                        <div className="flex items-baseline justify-between">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Pot</span>
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {open ? "Paying out in" : "Rains in"}{" "}
                                <span className="font-mono tabular-nums">{formatCountdown(secondsLeft)}</span>
                            </span>
                        </div>
                        <p className="text-3xl font-bold text-white mt-0.5">
                            <SokoAmount value={rain.pot} size={24} />
                        </p>

                        <dl className="mt-3 space-y-1 text-xs">
                            {[
                                { label: "Base", value: rain.base_amount },
                                { label: `From bets (${rain.contribution_percent}% of every wager)`, value: fromBets },
                                { label: "Tipped by players", value: rain.tips_total },
                            ].map((row) => (
                                <div key={row.label} className="flex justify-between gap-3">
                                    <dt className="text-gray-400 truncate">{row.label}</dt>
                                    <dd className="text-gray-300 font-mono tabular-nums shrink-0">
                                        {row.value.toLocaleString()}
                                    </dd>
                                </div>
                            ))}
                        </dl>

                        <div className="mt-3 pt-3 border-t border-white/5 flex justify-between gap-2 text-xs">
                            <span className="text-gray-400">
                                {rain.participant_count} {rain.participant_count === 1 ? "player" : "players"} joined
                            </span>
                            {rain.my_tip > 0 && (
                                <span className="text-green-300 font-semibold inline-flex items-center gap-1">
                                    You tipped <SokoAmount value={rain.my_tip} size={12} />
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Who can take part — always spelled out */}
                    <div
                        className={`bg-gray-800/50 rounded-xl p-3 text-xs leading-relaxed ${
                            eligibility.eligible ? "text-green-400" : "text-amber-400"
                        }`}
                    >
                        <div className="flex items-start gap-2">
                            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <div>
                                <span className="font-semibold">
                                    {!loggedIn
                                        ? "Log in to take part"
                                        : eligibility.eligible
                                            ? "You are eligible to join"
                                            : "You are not eligible to join"}
                                </span>
                                <div className="text-gray-400 mt-1">
                                    {eligibility.eligible
                                        ? "Anyone can tip. Joining is what puts you in the split."
                                        : `Play any game with Soko Points to become eligible.`}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* How it works */}
                    <div className="bg-gray-800/50 rounded-xl p-3 space-y-1">
                        <div className="flex items-center gap-1.5 text-gray-300 text-xs font-semibold">
                            <Info className="w-3.5 h-3.5 text-green-400" />
                            How the rain works
                        </div>
                        <ul className="text-xs text-gray-400 space-y-0.5 list-disc list-inside leading-relaxed">
                            <li>A rain drops {formatInterval(rain.interval_minutes)}.</li>
                            <li>
                                The pot is {rain.base_amount.toLocaleString()} to start, plus{" "}
                                {rain.contribution_percent}% of every Soko Points bet placed on SokoPlay, plus anything
                                players tip in.
                            </li>
                            <li>
                                <span className="text-gray-300">
                                    Joining only opens in the last {rain.join_window_minutes} minutes before the payout
                                </span>{" "}
                                — the JOIN button is locked until then.
                            </li>
                            <li>
                                <span className="text-gray-300">
                                    Only players who have bet Soko Points in the last {eligibility.days} days can join.
                                </span>
                            </li>
                            <li>Anyone can tip, eligible or not — but tipping does not put you in the split.</li>
                            <li>The pot is split equally between everyone who joined.</li>
                            <li>If nobody joins, the rain pays nothing and the next one starts fresh.</li>
                        </ul>
                    </div>

                    {/* Tip amount */}
                    <div>
                        <div className="flex items-baseline justify-between mb-1.5">
                            <label className="text-sm font-semibold text-green-300">Tip amount</label>
                            <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                                Balance: <SokoAmount value={balance} size={12} />
                            </span>
                        </div>
                        <div className="relative">
                            <input
                                type="number"
                                min={rain.min_tip}
                                max={Math.min(rain.max_tip, balance)}
                                step={1}
                                value={amount}
                                disabled={!loggedIn || tipping}
                                onChange={(e) => setAmount(clamp(Number(e.target.value)))}
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2.5 pr-16 text-gray-200 font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                            <button
                                type="button"
                                onClick={() => setAmount(clamp(balance))}
                                disabled={!loggedIn || tipping || balance < rain.min_tip}
                                title="Tip your whole balance"
                                className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-md text-xs font-bold tracking-wide text-green-400 bg-gray-700/60 hover:bg-gray-700 hover:text-green-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                MAX
                            </button>
                        </div>
                        <div className="flex gap-2 mt-2">
                            {QUICK_AMOUNTS.map((quick) => (
                                <button
                                    key={quick}
                                    type="button"
                                    onClick={() => setAmount(clamp(quick))}
                                    disabled={!loggedIn || tipping || balance < quick}
                                    className="flex-1 py-1.5 rounded-lg bg-gray-800/50 text-xs font-mono tabular-nums text-gray-300 hover:bg-gray-800 hover:text-green-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    {quick.toLocaleString()}
                                </button>
                            ))}
                        </div>
                    </div>

                    {error && (
                        <p className="text-xs text-red-300 bg-red-950/30 rounded-lg px-3 py-2">{error}</p>
                    )}

                    {/* Join — only while the window is open */}
                    {open && (
                        rain.joined ? (
                            <div className="flex items-center justify-center gap-2 rounded-xl bg-gray-800/50 py-2.5 text-sm font-semibold text-green-400">
                                <Check className="w-4 h-4" /> You&apos;re in this rain
                            </div>
                        ) : (
                            <button
                                onClick={join}
                                disabled={!loggedIn || !eligibility.eligible || joining}
                                className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 py-2.5 text-sm font-bold text-white hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
                            >
                                {joining && <Loader2 className="w-4 h-4 animate-spin" />}
                                JOIN THE RAIN
                            </button>
                        )
                    )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 p-4 border-t border-gray-800 shrink-0">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2.5 rounded-xl bg-gray-800/50 text-sm font-semibold text-gray-200 hover:bg-gray-800 transition-colors duration-200"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={submitTip}
                        disabled={!canTip}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-emerald-600 via-emerald-800 to-gray-900 text-sm font-bold text-white hover:shadow-lg hover:shadow-emerald-500/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
                    >
                        {tipping && <Loader2 className="w-4 h-4 animate-spin" />}
                        {loggedIn ? <>TIP <SokoAmount value={amount} size={14} /></> : "LOG IN TO TIP"}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
